import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  CheckSquare2,
  FileText,
  Loader2,
  PackageX,
} from "lucide-react";
import {
  useEditPurchaseReturnEntry,
  useGetAPurchaseReturnEntry,
  useSavePurchaseEntryReturn,
  useUpdatePurchaseEntryReturn,
} from "../service/purchaseEntryReturnService";
import { toast } from "react-toastify";
import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../utils/components/ui/breadcrumb";

// ── Schema ────────────────────────────────────────────────────────────────────

const PurchaseReturnProductSchema = z.object({
  id: z.number().default(0),
  purchaseEntryProductId: z.number().optional(),
  productId: z.number().optional(),
  returnQuantity: z.coerce
    .number()
    .positive("Must be greater than zero")
    .default(1),
  reason: z.string().default(""),
  unitPrice: z.number().optional(),
});

const PurchaseReturnSchema = z.object({
  purchaseEntryId: z.number().optional(),
  returnDate: z.string().optional(),
  purchaseReturnProductRequests: z
    .array(PurchaseReturnProductSchema)
    .optional(),
  attachment: z.string().optional(),
  invoiceNumber: z.string().optional(),
  vendorName: z.string().optional(),
  paymentMode: z.string().min(1, "Payment mode is required"),
  chqNumber: z.string().optional(),
  cardNumber: z.string().optional(),
  onlinePaymentRefNo: z.string().optional(),
});

export type PurchaseReturnSchemaT = z.infer<typeof PurchaseReturnSchema>;

// ── Component ─────────────────────────────────────────────────────────────────

function NewPurchaseFormReturn() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = window.location.pathname.includes("/edit/");

  const { mutate: savePurchaseReturn, isPending: isSavePending } =
    useSavePurchaseEntryReturn();
  const { mutate: updatePurchaseReturn, isPending: isUpdatePending } =
    useUpdatePurchaseEntryReturn();

  const { data: purchaseReturnData } = isEdit
    ? useEditPurchaseReturnEntry({ purchaseNumber: id })
    : useGetAPurchaseReturnEntry({ purchaseNumber: id, vendorId: Number(searchParams.get("vendorId")) });

  const isMutationPending = isSavePending || isUpdatePending;

  // Products list — create uses purchaseEntryProductResponse, edit uses products from PurchaseReturnResponseT
  const products: any[] = isEdit
    ? (purchaseReturnData as any)?.products || []
    : (purchaseReturnData as any)?.purchaseEntryProductResponse || [];

  // Track which product indices are selected for return
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    new Set(),
  );

  const toggleProduct = (idx: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIndices.size === products.length) {
      setSelectedIndices(new Set());
    } else {
      setSelectedIndices(new Set(products.map((_, i) => i)));
    }
  };

  // Convert dd-MM-yyyy to yyyy-MM-dd for HTML date input
  const toInputDate = (value: string | undefined) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const parts = value.split("-");
    if (parts.length === 3 && parts[0].length <= 2) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return value;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    reset,
  } = useForm<PurchaseReturnSchemaT>({
    resolver: zodResolver(PurchaseReturnSchema) as any,
    defaultValues: {
      returnDate: isEdit
        ? toInputDate((purchaseReturnData as any)?.returnDate)
        : toInputDate((purchaseReturnData as any)?.purchaseDate),
      purchaseReturnProductRequests: products.map((p: any) => ({
        id: p.id || 0,
        purchaseEntryProductId: p.purchaseEntryProductId || p.id,
        productId: p.productId,
        returnQuantity: isEdit ? p.returnQuantity || 1 : 1,
        reason: p.reason || "",
        unitPrice: p.unitPrice,
      })),
    },
  });

  // Reset form when data loads (defaultValues don't update after initial render)
  useEffect(() => {
    if (purchaseReturnData && products.length > 0) {
      reset({
        returnDate: isEdit
          ? toInputDate((purchaseReturnData as any)?.returnDate)
          : toInputDate((purchaseReturnData as any)?.purchaseDate),
        purchaseReturnProductRequests: products.map((p: any) => ({
          id: p.id || 0,
          purchaseEntryProductId: p.purchaseEntryProductId || p.id,
          productId: p.productId,
          returnQuantity: isEdit ? p.returnQuantity || 1 : 1,
          reason: p.reason || "",
          unitPrice: p.unitPrice,
        })),
      });
      // In edit mode, pre-select all products
      if (isEdit) {
        setSelectedIndices(new Set(products.map((_: any, i: number) => i)));
      }
    }
  }, [purchaseReturnData]);

  const watchedRequests = watch("purchaseReturnProductRequests") || [];
  const paymentMode = watch("paymentMode");

  // Running return total for selected items
  const returnTotal = Array.from(selectedIndices).reduce((sum, idx) => {
    const product = products[idx];
    const qty = Number(watchedRequests[idx]?.returnQuantity || 1);
    return sum + (product?.unitPrice || 0) * qty;
  }, 0);

  const onSubmit = (data: PurchaseReturnSchemaT) => {
    if (selectedIndices.size === 0) {
      toast.error("Please select at least one product to return.");
      return;
    }

    const missingReason = Array.from(selectedIndices).some(
      (idx) => !data.purchaseReturnProductRequests?.[idx]?.reason?.trim(),
    );
    if (missingReason) {
      toast.error("Please provide a reason for each selected product.");
      return;
    }

    if (isEdit) {
      // Edit mode — use products array from PurchaseReturnResponseT
      const editData = purchaseReturnData as any;
      const postDataUpdate: any = {
        id: editData?.id,
        purchaseEntryId: editData?.purchaseEntryId || 0,
        returnDate: editData?.returnDate || "",
        purchaseReturnProductRequests: Array.from(selectedIndices).map(
          (idx) => {
            const item = data.purchaseReturnProductRequests![idx];
            const product = products[idx] || {};
            return {
              id: product?.id ?? 0,
              purchaseEntryProductId: product?.purchaseEntryProductId ?? 0,
              productId: product?.productId ?? 0,
              returnQuantity: Number(item?.returnQuantity || 1),
              unit: product?.unit || "",
              conversionToBaseUnit: Number(product?.conversionToBaseUnit || 1),
              baseReturnQuantity:
                Number(item?.returnQuantity || 1) *
                Number(product?.conversionToBaseUnit || 1),
              reason: item?.reason || "",
              unitPrice: product?.unitPrice ?? 0,
              tax: product?.tax ?? 0,
              discount: product?.discount ?? 0,
            };
          },
        ),
        attachment: data.attachment || "",
        paymentMode: data.paymentMode || "",
        chqNumber: data.chqNumber || "",
        cardNumber: data.cardNumber || "",
        onlinePaymentRefNo: data.onlinePaymentRefNo || "",
      };

      updatePurchaseReturn(postDataUpdate, {
        onSuccess: () => {
          toast.success("Purchase return updated successfully!");
          navigate("/purchase-return");
        },
        onError: (error: Error) => {
          toast.error(`Error updating purchase return: ${error.message}`);
        },
      });
    } else {
      // Create mode
      const createData = purchaseReturnData as any;
      const postData: any = {
        purchaseEntryId: createData?.id || 0,
        returnDate: createData?.purchaseDate || "",
        paymentMode: data.paymentMode || "",
        chqNumber: data.chqNumber || "",
        cardNumber: data.cardNumber || "",
        onlinePaymentRefNo: data.onlinePaymentRefNo || "",
        purchaseReturnProductRequests: Array.from(selectedIndices).map(
          (idx) => {
            const item = data.purchaseReturnProductRequests![idx];
            const product = products[idx];
            const returnQty = Number(item?.returnQuantity || 1);
            const conversion = Number(product?.conversionToBaseUnit || 1);
            return {
              purchaseEntryProductId: product?.id ?? 0,
              productId: product?.productId ?? 0,
              returnQuantity: returnQty,
              reason: item?.reason || "",
              unitPrice: product?.unitPrice ?? 0,
              discount: product?.discount ?? 0,
              tax: product?.tax ?? 0,
              unit: product?.unit || "",
              conversionToBaseUnit: conversion,
              baseReturnQuantity: returnQty * conversion,
            };
          },
        ),
        attachment: data.attachment || "",
      };

      savePurchaseReturn(postData, {
        onSuccess: () => {
          toast.success("Purchase return saved successfully!");
          navigate("/purchase-return");
        },
        onError: (error: Error) => {
          toast.error(`Error saving purchase return: ${error.message}`);
        },
      });
    }
  };

  const allSelected =
    products.length > 0 && selectedIndices.size === products.length;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="min-h-full py-6 px-1 space-y-6"
    >
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/">Home</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href="/purchase-return">
              Purchase Return
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>
              {isEdit ? "Edit Purchase Return" : "Create Purchase Return"}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Page header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/purchase-return")}
          className="p-2 rounded-[10px] border border-[#D7E2F5] text-[#52627C] hover:bg-[#F5F8FD] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[#1A5EF9]">
            {isEdit ? "Edit Purchase Return" : "Create Purchase Return"}
          </h1>
          <p className="text-sm text-[#7D8998] mt-0.5">
            Select products and enter return details
          </p>
        </div>
      </div>

      {/* Purchase / Vendor info card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
        <h2 className="text-base font-semibold text-[#21314A] mb-5 flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#1A5EF9]" />
          Purchase Details
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Vendor */}
          <div>
            <label className="text-xs font-medium text-[#7D8998] mb-1.5 flex items-center gap-1">
              <Building2 className="w-3 h-3" /> Vendor
            </label>
            <div className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 flex items-center text-sm text-[#23324D] font-medium">
              {(purchaseReturnData as any)?.vendorName || "—"}
            </div>
          </div>

          {/* Purchase number */}
          <div>
            <label className="text-xs font-medium text-[#7D8998] mb-1.5 flex items-center gap-1">
              <FileText className="w-3 h-3" /> Purchase Number
            </label>
            <div className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 flex items-center text-sm text-[#23324D] font-medium">
              {(purchaseReturnData as any)?.returnNumber ||
                (purchaseReturnData as any)?.purchaseNumber ||
                "—"}
            </div>
          </div>

          {/* Purchase date */}
          <div>
            <label className="text-xs font-medium text-[#7D8998] mb-1.5 flex items-center gap-1">
              <CalendarDays className="w-3 h-3" /> Purchase Date
            </label>
            <div className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 flex items-center text-sm text-[#23324D]">
              {(purchaseReturnData as any)?.purchaseDate || "—"}
            </div>
          </div>

          {/* Return date */}
          <div>
            <label className="text-xs font-medium text-[#7D8998] mb-1.5 flex items-center gap-1">
              <CalendarDays className="w-3 h-3" /> Return Date
            </label>
            <input
              type="date"
              {...register("returnDate")}
              className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
            />
          </div>
        </div>

        {/* Payment mode row */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-xs font-medium text-[#7D8998] mb-1.5 block">
              Payment Mode <span className="text-red-400">*</span>
            </label>
            <Controller
              name="paymentMode"
              control={control}
              defaultValue={(purchaseReturnData as any)?.paymentMode || ""}
              render={({ field }) => (
                <select
                  {...field}
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                >
                  <option value="">Select mode</option>
                  <option value="CASH">Cash</option>
                  <option value="CARD">Card</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="CREDIT">Credit</option>
                  <option value="ONLINE_PAYMENT">Online</option>
                  <option value="MIXED">Mixed</option>
                </select>
              )}
            />
            {errors.paymentMode && (
              <p className="text-red-500 text-xs mt-1">
                {errors.paymentMode.message}
              </p>
            )}
          </div>

          {paymentMode === "ONLINE_PAYMENT" && (
            <div>
              <label className="text-xs font-medium text-[#7D8998] mb-1.5 block">
                Online Ref No
              </label>
              <input
                type="text"
                {...register("onlinePaymentRefNo")}
                placeholder="Reference number"
                className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
              />
            </div>
          )}
          {paymentMode === "CARD" && (
            <div>
              <label className="text-xs font-medium text-[#7D8998] mb-1.5 block">
                Card Number
              </label>
              <input
                type="text"
                {...register("cardNumber")}
                placeholder="Card number"
                className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
              />
            </div>
          )}
          {paymentMode === "CHEQUE" && (
            <div>
              <label className="text-xs font-medium text-[#7D8998] mb-1.5 block">
                Cheque Number
              </label>
              <input
                type="text"
                {...register("chqNumber")}
                placeholder="Cheque number"
                className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
              />
            </div>
          )}
        </div>
      </div>

      {/* Products selection card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] overflow-hidden">
        {/* Card header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEF2FA] bg-[#F8FAFF]">
          <div className="flex items-center gap-2">
            <PackageX className="w-4 h-4 text-[#1A5EF9]" />
            <h2 className="text-base font-semibold text-[#21314A]">
              Select Products to Return
            </h2>
            {selectedIndices.size > 0 && (
              <span className="inline-flex items-center rounded-full bg-[#E8EFFF] text-[#1A5EF9] text-xs font-semibold px-2.5 py-0.5">
                {selectedIndices.size} selected
              </span>
            )}
          </div>
          {products.length > 0 && (
            <button
              type="button"
              onClick={selectAll}
              className="text-xs font-medium text-[#1A5EF9] hover:underline"
            >
              {allSelected ? "Deselect all" : "Select all"}
            </button>
          )}
        </div>

        {products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[#8D98A7]">
            <PackageX className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">No products available for return</p>
          </div>
        ) : (
          <div className="divide-y divide-[#EEF2FA]">
            {products.map((product, idx) => {
              const isSelected = selectedIndices.has(idx);
              const watchedQty = Number(
                watchedRequests[idx]?.returnQuantity || 1,
              );
              const lineTotal = (product.unitPrice || 0) * watchedQty;

              return (
                <div
                  key={product.id ?? idx}
                  className={`transition-colors ${
                    isSelected
                      ? "bg-[#F5F8FF] border-l-4 border-l-[#1A5EF9]"
                      : "bg-white border-l-4 border-l-transparent hover:bg-[#FAFCFF]"
                  }`}
                >
                  <div className="px-6 py-4">
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      <div className="pt-0.5">
                        <input
                          type="checkbox"
                          id={`select-product-${idx}`}
                          checked={isSelected}
                          onChange={() => toggleProduct(idx)}
                          className="w-4 h-4 rounded accent-[#1A5EF9] cursor-pointer"
                        />
                      </div>

                      {/* Product info */}
                      <label
                        htmlFor={`select-product-${idx}`}
                        className="flex-1 cursor-pointer"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          {/* Left: name + badges */}
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-sm font-semibold truncate ${
                                isSelected ? "text-[#1A5EF9]" : "text-[#21314A]"
                              }`}
                            >
                              {product.productName || "—"}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-1.5">
                              {product.unit && (
                                <span className="inline-flex items-center rounded-full bg-[#F0F4FF] text-[#4B6EF5] text-xs px-2 py-0.5">
                                  Unit: {product.unit}
                                </span>
                              )}
                              <span className="inline-flex items-center rounded-full bg-[#F0FAF4] text-[#0E9F6E] text-xs px-2 py-0.5">
                                Purchased:{" "}
                                {product.purchasedQuantity ?? product.quantity}
                              </span>
                              <span className="inline-flex items-center rounded-full bg-[#FFF7ED] text-[#D97706] text-xs px-2 py-0.5">
                                Available: {product.availableQuantity ?? "—"}
                                {product.availableQuantityUnit
                                  ? ` ${product.availableQuantityUnit}`
                                  : ""}
                              </span>
                              {product.tax ? (
                                <span className="inline-flex items-center rounded-full bg-[#F5F5F5] text-[#6B7280] text-xs px-2 py-0.5">
                                  Tax {product.tax}%
                                </span>
                              ) : null}
                              {product.discount ? (
                                <span className="inline-flex items-center rounded-full bg-[#F5F5F5] text-[#6B7280] text-xs px-2 py-0.5">
                                  Discount {product.discount}%
                                </span>
                              ) : null}
                            </div>
                          </div>

                          {/* Right: unit price */}
                          <div className="text-right shrink-0">
                            <p className="text-xs text-[#7D8998]">Unit Price</p>
                            <p className="text-sm font-bold text-[#21314A]">
                              {Number(product.unitPrice || 0).toFixed(3)}
                            </p>
                          </div>
                        </div>
                      </label>
                    </div>

                    {/* Expanded return inputs — only when selected */}
                    {isSelected && (
                      <div className="mt-4 ml-8 grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 rounded-[14px] bg-white border border-[#D8E4FB]">
                        {/* Return Quantity */}
                        <div>
                          <label className="text-xs font-medium text-[#52627C] mb-1.5 block">
                            Return Quantity{" "}
                            <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="number"
                            min="0.0001"
                            step="any"
                            max={product.availableQuantity ?? undefined}
                            placeholder="e.g. 1"
                            {...register(
                              `purchaseReturnProductRequests.${idx}.returnQuantity`,
                              { valueAsNumber: true },
                            )}
                            className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#1A5EF9]"
                          />
                          {errors.purchaseReturnProductRequests?.[idx]
                            ?.returnQuantity && (
                            <p className="text-red-500 text-xs mt-1">
                              {
                                errors.purchaseReturnProductRequests[idx]
                                  .returnQuantity?.message
                              }
                            </p>
                          )}
                        </div>

                        {/* Reason */}
                        <div className="sm:col-span-2">
                          <label className="text-xs font-medium text-[#52627C] mb-1.5 block">
                            Reason for Return{" "}
                            <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. Damaged, Wrong item…"
                            {...register(
                              `purchaseReturnProductRequests.${idx}.reason`,
                            )}
                            className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#1A5EF9]"
                          />
                          {errors.purchaseReturnProductRequests?.[idx]
                            ?.reason && (
                            <p className="text-red-500 text-xs mt-1">
                              {
                                errors.purchaseReturnProductRequests[idx].reason
                                  ?.message
                              }
                            </p>
                          )}
                        </div>

                        {/* Return total preview */}
                        <div className="sm:col-span-3 flex justify-end">
                          <div className="text-right">
                            <p className="text-xs text-[#7D8998]">
                              Return Total
                            </p>
                            <p className="text-base font-bold text-[#1A5EF9]">
                              {lineTotal.toFixed(3)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Summary + actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Return total summary */}
        {selectedIndices.size > 0 && (
          <div className="rounded-[16px] border border-[#D8E4FB] bg-[#F0F4FF] px-5 py-3 flex items-center gap-4">
            <CheckSquare2 className="w-5 h-5 text-[#1A5EF9] shrink-0" />
            <div>
              <p className="text-xs text-[#4B6EF5]">
                {selectedIndices.size}{" "}
                {selectedIndices.size === 1 ? "product" : "products"} selected
              </p>
              <p className="text-base font-bold text-[#1A5EF9]">
                Return Total: {returnTotal.toFixed(3)}
              </p>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 ml-auto">
          <button
            type="button"
            onClick={() => navigate("/purchase-return")}
            className="h-11 rounded-[12px] border border-[#D7E2F5] px-6 text-[#52627C] hover:bg-[#F5F8FD] font-medium text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isMutationPending || selectedIndices.size === 0}
            className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE] font-medium text-sm flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isMutationPending && <Loader2 className="animate-spin w-4 h-4" />}
            {isEdit ? "Update Return" : "Save Return"}
          </button>
        </div>
      </div>
    </form>
  );
}

export default NewPurchaseFormReturn;
